// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import CheckoutHeaderPhone from "@guestsupply/App/Components/Checkout/CheckoutHeaderPhone";
import StyledOverlay from "@guestsupply/App/Components/Common/StyledOverlay";
import XngUtilities from "@guestsupply/App/Components/Common/XngUtilities";
import HeaderClickable, { HeaderClickableStyle } from "@guestsupply/App/Components/Header/HeaderClickable";
import xHeaderUpdate from "@guestsupply/App/Handlers/XNG/Header";
import useSize from "@guestsupply/App/Hooks/useSize";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getLogoImageWidget, xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@guestsupply/XDL/constants";
import Search from "@guestsupply/XDL/Icons/Search";
import Search2 from "@guestsupply/XDL/Icons/Search2";
import { parseBoolean, xClassNames, xScreen } from "@guestsupply/XDL/Utilities";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { loadPage } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import Transition, { TransitionStatus } from "react-transition-group/Transition";
import styled, { css } from "styled-components";
/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    showSearchSectionOnMobile: (state as CustomAppState).xHeader.showSearchSectionOnMobile,
    registerMenu: (state as CustomAppState).xHeader.registerMenu,
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    // XNG-Changes: adding logo to print view
    logoImageWidget: getLogoImageWidget(state),
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    location: getLocation(state),
    isSticky: (state as CustomAppState).xHeader.isSticky,
    isAuthenticated: xIsAuthenticated(state),
    cartLines: getCurrentCartState(state).value?.cartLines,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    xHeaderUpdate,
    loadPage,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-CHANGES
    wrapper?: InjectableCss;
    wrapperPage?: MobiusPageProps;
    wrapperStyledWrapper?: InjectableCss;
    logoLinksSearchWrapper?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    orderGuideWrapper?: InjectableCss;
    signInWrapper?: InjectableCss;
    cartWrapper?: InjectableCss;
    languageWrapper?: InjectableCss;
    mainNavigationWrapper?: MobiusPageProps;
    headerLinkListWrapper?: InjectableCss;
    searchClickableWrapper?: InjectableCss;
    searchClickable?: HeaderClickableStyle;
    printLogo?: InjectableCss;
    printLogoImage?: ImgProps;
    navigationAndLinkedListWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            box-shadow: 0 1px 0 0 ${getColor("common.border")};
            margin-bottom: 12px;
            ${xScreen("lte", "md")} {
                position: sticky;
                top: 0;
                left: 0;
            }
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const RightSectionWrapper = styled.div<{ isShellEditing?: boolean } & InjectableCss>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: ${({ isShellEditing }) => `${isShellEditing ? "column" : "row"}`};
    gap: 24px;
    margin-left: 12px;
    ${xScreen("lte", "md")} {
        gap: 12px;
    }
`;

const LogoLinksSearchWrapper = styled.div<{ isSearchFullWidth?: boolean } & InjectableCss>`
    flex: 1;
`;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    xHeaderUpdate,
    showSearchSectionOnMobile,
    registerMenu,
    isTabletScreen,
    logoImageWidget,
    isCheckoutPage,
    isMobileScreen,
    loadPage,
    location,
    isSticky,
    isAuthenticated,
    cartLines,
}: Props) => {
    // <XNG-Changes>
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;
    const shellContext = useShellContext();
    const isEducationCustomer = parseBoolean(session.properties?.isEducationCustomer);

    // </XNG-Changes>

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    // XNG-Changes
    const toggleSearch = () => {
        xHeaderUpdate({ key: "showSearchSectionOnMobile", value: !showSearchSectionOnMobile });
    };

    useEffect(() => {
        const popSatateHandler = () => {
            hideAll();
        };
        window.addEventListener("popstate", popSatateHandler);
        return () => window.removeEventListener("popstate", popSatateHandler);
    }, []);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler} aria-label="View My Dashboard">
                            {translate("View My Dashboard")}
                        </Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);
    // <XNG-Changes>
    useEffect(() => {
        if (headerHeight) {
            xHeaderUpdate({ key: "headerHeight", value: `${headerHeight}px` });
        }
    }, [headerHeight]);
    // XNG-CHANGES : change the header structure
    const afterHeader = React.createRef<HTMLSpanElement>();
    // XNG-CHANGES GSS-805, GSS-749 : add tippy specific position on chrome only for non-sticky header
    let isChrome = false;
    if (typeof navigator !== "undefined") {
        isChrome = navigator && navigator?.userAgent?.includes("Chrome") && navigator?.vendor?.includes("Google Inc");
    }
    const isChromeAndNotSticky = isChrome && !isSticky;
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImageWidget && (
                <StyledWrapper {...styles.printLogo}>
                    <img alt={translate("Home")} src={logoImageWidget.logoImage} {...styles.printLogoImage} />
                </StyledWrapper>
            )}
            <StyledWrapper ref={headerRef} className="header-wrapper" {...styles.wrapper}>
                {/* {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>} */}
                <Page as="header" isHeader={true} {...styles.page} {...{ isCheckoutPage }}>
                    <Zone contentId={id} zoneName="GlobalWebsiteMessage" />
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <EagerLoadingLogoProvider value={true}>
                        <Page {...styles.wrapperPage} {...{ isCheckoutPage }}>
                            <Page>
                                <StyledWrapper
                                    {...styles.wrapperStyledWrapper}
                                    className="header-top-section-wrapper"
                                    {...{ isCheckoutPage }}
                                >
                                    <LogoLinksSearchWrapper
                                        isSearchFullWidth={showSearchSectionOnMobile && isMobileScreen}
                                    >
                                        <Zone contentId={id} zoneName="LogoLinksSearch" />
                                    </LogoLinksSearchWrapper>
                                    {!isCheckoutPage &&
                                        ((!showSearchSectionOnMobile && isMobileScreen) || !isMobileScreen) && (
                                            <RightSectionWrapper
                                                {...({ isShellEditing: shellContext.isEditing } as any)}
                                            >
                                                {!isEducationCustomer && (
                                                    <StyledWrapper {...styles.searchClickableWrapper}>
                                                        <HeaderClickable
                                                            className="mobile-search-button"
                                                            onClick={toggleSearch}
                                                            iconProps={{ src: Search }}
                                                            extendedStyles={styles.searchClickable}
                                                        />
                                                    </StyledWrapper>
                                                )}
                                                <StyledWrapper {...styles.orderGuideWrapper}>
                                                    <Zone contentId={id} zoneName="OrderGuide" />
                                                </StyledWrapper>
                                                <StyledWrapper
                                                    {...styles.signInWrapper}
                                                    className={xClassNames.append([
                                                        isChromeAndNotSticky && "is-chrome",
                                                        isAuthenticated && "authenticated",
                                                    ])}
                                                >
                                                    <Zone contentId={id} zoneName="SignIn" />
                                                </StyledWrapper>
                                                <StyledWrapper
                                                    {...styles.cartWrapper}
                                                    className={xClassNames.append([
                                                        isChromeAndNotSticky && "is-chrome",
                                                        !!cartLines?.length && "has-cartlines",
                                                    ])}
                                                >
                                                    <Zone contentId={id} zoneName="Cart" />
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.languageWrapper}>
                                                    <Zone contentId={id} zoneName="Language" />
                                                </StyledWrapper>
                                            </RightSectionWrapper>
                                        )}
                                    <CheckoutHeaderPhone />
                                </StyledWrapper>
                            </Page>
                        </Page>
                        {!isCheckoutPage && !isTabletScreen && (
                            <Page {...styles.mainNavigationWrapper}>
                                <StyledWrapper {...styles.navigationAndLinkedListWrapper}>
                                    <StyledWrapper>
                                        <Zone contentId={id} zoneName="MainNavigation" />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.headerLinkListWrapper}>
                                        <Zone contentId={id} zoneName="HeaderLinkList" />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </Page>
                        )}
                    </EagerLoadingLogoProvider>
                </Page>
                <Page>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </Page>
                <span ref={afterHeader} tabIndex={-1} />
            </StyledWrapper>
            {/* <XNG-Changes> */}
            <XngUtilities />
            <Transition
                mountOnEnter
                unmountOnExit
                in={registerMenu && registerMenu.length > 0}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <StyledOverlay
                        className={registerMenu && isTabletScreen ? "header-overlay" : ""}
                        transitionState={state}
                    />
                )}
            </Transition>
            {/* </XNG-Changes> */}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
